import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import type { FinanceHealthDto } from '@signalvote/shared';
import { useFinanceHealth } from '@/hooks/queries/useFinanceDeadlines';
import { formatDate } from '../utils';
import { useOpenAmendments } from './useOpenAmendments';

/**
 * Reports awaiting an amendment, kept ON the screens built for showing what needs doing.
 *
 * Changing something inside a period the treasurer has already filed flips that report
 * `Filed → NeedsAmendment`. That is the most action-requiring state in the system — and it
 * used to be the one state both hubs excluded, because every filed-report surface queries
 * `status === Filed`. The report silently vanished from the dashboard, leaving only a pulsing
 * badge on the reports list, a page the dashboard actively steers away from. Worse for being
 * rare: a treasurer who hits this twice a year has no habit to fall back on.
 *
 * Reads the finance-health payload both host surfaces already fetch and invalidate on ledger
 * changes — the very changes that cause the flip — so no new fetch is introduced and the card
 * can never lag the flip. Self-hides when nothing needs amending, which is what makes it safe
 * to mount unconditionally on the dashboard and the close hub.
 *
 * Deep-links into the EXISTING amend affordance on the reports list (the Amend button with its
 * jurisdiction-aware confirm dialog), naming the report so the treasurer knows which row to act
 * on — the same posture as `BoundaryStragglerAlerts`. No parallel amend flow is built here.
 */
const NONE: FinanceHealthDto['reportsNeedingAmendment'] = [];

export function NeedsAmendmentAlerts() {
  const health = useFinanceHealth();
  // An additive alert: until health answers it stays out; the page's filing-status surface
  // (the dashboard card, the close masthead) owns the failure.
  const answered = health.status === 'ready' ? health.data : null;
  const listed = answered?.reportsNeedingAmendment ?? NONE;
  // A report whose amendment is already started waits on that amendment, which its own card
  // shows; offering "Amend" again would send the treasurer in a circle.
  const amending = useOpenAmendments();
  const reports = listed.filter(
    (r) => !amending.some((a) => a.periodStart === r.periodStart && a.periodEnd === r.periodEnd),
  );
  // The TRUE count, which the capped row list may understate. Falls back to the rows
  // themselves only if an older payload omits it, so the headline is never blank.
  const total = (answered?.reportsNeedingAmendmentTotal ?? listed.length) - (listed.length - reports.length);
  const unlisted = Math.max(total - reports.length, 0);

  if (reports.length === 0) return null;

  return (
    <div
      data-testid="needs-amendment-alerts"
      data-slot="needs-amendment-alerts"
      className="rounded-lg border border-amber-200 bg-amber-50 p-4"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <p className="text-[13px] font-semibold text-amber-900">
              {total === 1
                ? 'A report you already filed needs an amendment'
                : `${total} reports you already filed need an amendment`}
            </p>
            {/* Verbatim the reports-list badge label (reportUtils `statusBadge`), so the
                state reads identically wherever the treasurer meets it — and never as
                the settled green "Filed". */}
            <span
              data-testid="needs-amendment-badge"
              className="shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700"
            >
              Needs Amendment
            </span>
          </div>
          <p className="mt-0.5 text-[12.5px] leading-snug text-amber-800">
            Something changed inside a period you&apos;d already filed, so the filing no longer
            matches your records. Amending brings them back in line.
          </p>

          <ul className="mt-3 flex flex-col gap-2">
            {reports.map((r) => (
              <li
                key={r.reportId}
                data-testid="needs-amendment-row"
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-md border border-amber-200 bg-white px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-slate-900">{r.reportName}</div>
                  <div className="mt-0.5 text-[12px] text-slate-500">
                    Covers {formatDate(r.periodStart)} – {formatDate(r.periodEnd)}
                    {/* A report with no filed stamp still belongs here — print nothing
                        rather than an "Invalid Date". */}
                    {r.filedDate && ` · filed ${formatDate(r.filedDate)}`}
                    {r.amendmentNumber > 0 && ` · amendment #${r.amendmentNumber}`}
                  </div>
                </div>

                <Link
                  to="/admin/finance/reports"
                  data-testid={`needs-amendment-amend-link-${r.reportId}`}
                  className="shrink-0 text-[12.5px] font-medium text-amber-700 underline hover:text-amber-800"
                >
                  Amend {r.reportName}
                </Link>
              </li>
            ))}
          </ul>

          {/* The payload caps the rows it ships. Without this the capped-off reports would
              vanish from the very surface this card exists to keep them on. */}
          {unlisted > 0 && (
            <Link
              to="/admin/finance/reports"
              data-testid="needs-amendment-overflow"
              className="mt-2 inline-block text-[12.5px] font-medium text-amber-700 underline hover:text-amber-800"
            >
              and {unlisted} more on your reports list
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
